/**
 * TiTiler COG raster for Mapbox GL.
 * @see https://developmentseed.org/titiler/endpoints/cog/
 */

import eldaSnowTitilerColormap from '../constants/eldaSnowTitilerColormap.json'
import { fetchTitilerColorMapList } from './titilerColorMapsApi.js'

export const ELDA_SNOW = Object.freeze({
  rescale: '0,6',
  nodata: '-9999',
  rescaleFallbackNonElda: '-10,10',
})

const titilerColormapIdsByBase = new Map()

// trim titiler base url and strip a trailing slash
function normalizeTitilerBase(titilerBase) {
  return (titilerBase || '').trim().replace(/\/$/, '')
}

// resolve the titiler base url for the browser from env with a dev fallback
export function getTitilerBaseUrl() {
  const raw = import.meta.env.VITE_TITILER_URL
  const t = (raw ?? '').trim().replace(/\/$/, '')
  if (t) return t
  if (import.meta.env.DEV) return '/titiler'
  return ''
}

// rewrite absolute titiler tile urls to use the local dev proxy path when needed
function rewriteTitilerTileUrlsForDevProxy(tiles, titilerBase) {
  if (!Array.isArray(tiles) || tiles.length === 0) return tiles
  const b = normalizeTitilerBase(titilerBase)
  if (!b.startsWith('/') || typeof window === 'undefined') return tiles
  const origin = window.location.origin

  return tiles.map(tileUrl => {
    const iCog = tileUrl.indexOf('/cog/')
    const iMosaic = tileUrl.indexOf('/mosaicjson/')
    // Pick the first matching segment (whichever is found).
    const i =
      iCog >= 0 && iMosaic >= 0
        ? Math.min(iCog, iMosaic)
        : iCog >= 0
          ? iCog
          : iMosaic
    if (i === -1) return tileUrl
    return `${origin}${b}${tileUrl.slice(i)}`
  })
}

function parseTitilerStyleField(ts) {
  if (ts == null) return null
  if (typeof ts === 'string') {
    try {
      const p = JSON.parse(ts)
      return typeof p === 'object' && p !== null && !Array.isArray(p) ? p : null
    } catch {
      return null
    }
  }
  if (typeof ts === 'object' && !Array.isArray(ts)) return ts
  return null
}

// return true if the layer is a snow depth COG (path/name, or station default style Neige ELDA en base)
export function isSnowDepthTitilerLayer(layer) {
  const name = (layer?.layerName || '').toLowerCase()
  if (name.includes('snow_depth')) return true

  const s3raw = layer?.s3_url || layer?.s3Url || ''
  const s3 = String(s3raw).toLowerCase()
  let decoded = s3
  try {
    decoded = decodeURIComponent(s3)
  } catch {
    /* ignore */
  }
  if (decoded.includes('snow_depth') || s3.includes('snow_depth')) return true

  const style = parseTitilerStyleField(layer?.titiler_style)
  if (!style) return false
  const cm = String(style.colormapName || style.colormap_name || '').trim()
  if (cm === 'elda_snow_mnt') return true
  return false
}

// build a min,max rescale pair from a titiler statistics band (percentiles or min/max)
function rescaleFromStatsBand(band) {
  if (!band || typeof band !== 'object') {
    return null
  }
  const pct = band.percentiles
  let low =
    band.percentile_2 ??
    band.percentile_02 ??
    (pct && (pct['2'] ?? pct[2])) ??
    null
  let high = band.percentile_98 ?? (pct && (pct['98'] ?? pct[98])) ?? null
  if (
    low == null ||
    high == null ||
    !Number.isFinite(low) ||
    !Number.isFinite(high)
  ) {
    low = band.min
    high = band.max
  }
  if (
    low == null ||
    high == null ||
    !Number.isFinite(low) ||
    !Number.isFinite(high)
  ) {
    return null
  }
  if (high <= low) {
    const pad = Math.max(1e-4, Math.abs(low) * 1e-6 + 1e-4)
    high = low + pad
  }
  return { low, high }
}

// pick the first band statistics object from a titiler /cog/statistics json payload
function pickStatisticsBand(data) {
  if (!data || typeof data !== 'object') {
    return null
  }
  const nested = data.statistics
  if (nested && typeof nested === 'object') {
    const b =
      nested.b1 ??
      nested['1'] ??
      Object.values(nested).find(
        v =>
          v &&
          typeof v === 'object' &&
          !Array.isArray(v) &&
          ('min' in v || 'percentile_2' in v || 'percentiles' in v),
      )
    if (b) return b
  }
  return (
    data.b1 ??
    data['1'] ??
    Object.values(data).find(
      v =>
        v &&
        typeof v === 'object' &&
        !Array.isArray(v) &&
        ('min' in v || 'percentile_2' in v || 'percentiles' in v),
    )
  )
}

// fetch rescale min,max from titiler /cog/info when band min/max are available
async function fetchSnowRescaleFromInfo(s3Url, titilerBase) {
  const base = normalizeTitilerBase(titilerBase)
  const params = new URLSearchParams()
  params.set('url', s3Url.trim())
  const url = `${base}/cog/info?${params.toString()}`
  try {
    const res = await fetch(url)
    if (!res.ok) {
      return null
    }
    const data = await res.json()
    const band =
      data.band_metadata?.['1'] ??
      data.band_metadata?.b1 ??
      data.bands?.[0] ??
      null
    const stats = band?.statistics ?? band?.stats ?? band
    if (!stats || typeof stats !== 'object') {
      return null
    }
    const low = stats.min ?? stats.Min
    const high = stats.max ?? stats.Max
    if (
      low == null ||
      high == null ||
      !Number.isFinite(low) ||
      !Number.isFinite(high)
    ) {
      return null
    }
    if (high <= low) {
      return `${low},${low + 1e-4}`
    }
    return `${low},${high}`
  } catch {
    return null
  }
}

// fetch rescale min,max from titiler /cog/statistics (p2 and p98)
async function fetchSnowRescaleFromStatistics(s3Url, titilerBase) {
  const base = normalizeTitilerBase(titilerBase)
  if (!base || !s3Url) {
    return null
  }

  const params = new URLSearchParams()
  params.set('url', s3Url.trim())
  params.append('bidx', '1')
  params.append('p', '2')
  params.append('p', '98')
  params.set('max_size', '2048')

  const url = `${base}/cog/statistics?${params.toString()}`
  try {
    const res = await fetch(url)
    if (!res.ok) {
      return null
    }

    const data = await res.json()
    const band = pickStatisticsBand(data)
    const pair = rescaleFromStatsBand(band)
    if (pair) {
      return `${pair.low},${pair.high}`
    }
    return null
  } catch {
    return null
  }
}

// resolve snow rescale string from statistics, falling back to /cog/info
async function fetchSnowRescale(s3Url, titilerBase) {
  const r = await fetchSnowRescaleFromStatistics(s3Url, titilerBase)
  if (r) {
    return r
  }
  return fetchSnowRescaleFromInfo(s3Url, titilerBase)
}

// load and cache titiler colormap ids per base url (list endpoint)
async function getTitilerColormapIdsCached(titilerBase) {
  const base = normalizeTitilerBase(titilerBase)
  if (!base) return []
  if (!titilerColormapIdsByBase.has(base)) {
    titilerColormapIdsByBase.set(
      base,
      fetchTitilerColorMapList(base).catch(() => []),
    )
  }
  return titilerColormapIdsByBase.get(base)
}

// resolve effective snow colormap id from saved style, env, or default
function getEffectiveSnowColormapName(styleOverride) {
  return (
    styleOverride?.colormapName?.trim() ||
    import.meta.env.VITE_TITILER_SNOW_COLORMAP?.trim() ||
    'elda_snow_mnt'
  )
}

// return true when the ELDA Neige MNT ramp (not another colormap) is selected
function usesEldaPhysicalSnowColormap(styleOverride) {
  return getEffectiveSnowColormapName(styleOverride) === 'elda_snow_mnt'
}

// choose inline json colormap vs colormap_name for elda snow depending on the server
async function resolveSnowColormapApply(titilerBase, styleOverride) {
  const cmUser = styleOverride?.colormapName?.trim()
  const colormap =
    cmUser ||
    import.meta.env.VITE_TITILER_SNOW_COLORMAP?.trim() ||
    'elda_snow_mnt'

  if (colormap !== 'elda_snow_mnt') {
    return { kind: 'name', name: colormap }
  }

  const base = normalizeTitilerBase(titilerBase)
  if (!base) {
    return { kind: 'inline' }
  }

  const ids = await getTitilerColormapIdsCached(base)
  if (ids.includes('elda_snow_mnt')) {
    return { kind: 'name', name: 'elda_snow_mnt' }
  }

  return { kind: 'inline' }
}

// resolve nodata query value for snow layers from env, with default and off switch
export function resolveSnowNodataQueryParam() {
  const raw = import.meta.env.VITE_TITILER_SNOW_NODATA
  const val =
    raw !== undefined && String(raw).trim() !== ''
      ? String(raw).trim()
      : ELDA_SNOW.nodata
  const off = val.toLowerCase() === 'off' || val.toLowerCase() === 'false'
  if (off) return null

  const normalized = String(val).trim()
  const isSpecialLiteral = /^(-)?(inf|nan)$/i.test(normalized)
  const isNumericLiteral = /^-?\d+(\.\d+)?$/.test(normalized)
  if (!isSpecialLiteral && !isNumericLiteral) return ELDA_SNOW.nodata

  return normalized
}

// append titiler rendering params (rescale, colormap, nodata, algorithm) to tilejson search params
function appendTitilerRenderingParams(
  params,
  layer,
  snowRescale = null,
  styleOverride = null,
  snowColormapApply = null,
) {
  // ortho_rgb : RGB natif → pas de colormap, pas de rescale, pas d'algorithm.
  // (Les mosaïques mono-bande, ex. MNT/MNS, passent par le rescale/colormap comme un raster simple.)
  if (layer?.raster_type === 'ortho_rgb') return

  const renderMode = String(styleOverride?.renderMode || 'default')

  // Algorithm derivative (hillshade, slope, contours) — most return RGBA natively,
  // but `slope` outputs a single-band float (0..90°) that needs colormap + rescale.
  if (renderMode !== 'default') {
    params.set('algorithm', renderMode)
    if (
      styleOverride?.renderParams &&
      typeof styleOverride.renderParams === 'object' &&
      Object.keys(styleOverride.renderParams).length > 0
    ) {
      params.set('algorithm_params', JSON.stringify(styleOverride.renderParams))
    }
    // Slope (and aspect when added) outputs single-band float → needs colormap+rescale.
    if (renderMode === 'slope') {
      const cmUserSlope =
        styleOverride?.colormapName?.trim() || 'turbo_r'
      params.set('colormap_name', cmUserSlope)
      // Manual rescale wins, otherwise default to 0..60° (most ski-relevant range).
      const manualUiSlope =
        styleOverride?.rescaleMode === 'manual' &&
        typeof styleOverride?.rescaleManual === 'string'
          ? styleOverride.rescaleManual.trim()
          : ''
      params.set('rescale', manualUiSlope || '0,60')
    }
    return
  }

  // Default rendering : reuse existing snow / non-snow logic
  const cmUser = styleOverride?.colormapName?.trim()
  if (!layer || !isSnowDepthTitilerLayer(layer)) {
    // Manual rescale takes precedence over auto/value_min-max.
    const manualUi =
      styleOverride?.rescaleMode === 'manual' &&
      typeof styleOverride?.rescaleManual === 'string'
        ? styleOverride.rescaleManual.trim()
        : ''
    // Auto: use value_min/value_max captured by Django at upload time (in DB).
    // Explicit null/undefined check — Number(null) === 0 would be a false positive.
    const rawMin = layer?.value_min
    const rawMax = layer?.value_max
    const vMin = rawMin != null ? Number(rawMin) : NaN
    const vMax = rawMax != null ? Number(rawMax) : NaN
    const hasStats = Number.isFinite(vMin) && Number.isFinite(vMax) && vMax > vMin
    const autoRescale = hasStats ? `${vMin},${vMax}` : ''
    const defRescale = import.meta.env.VITE_TITILER_DEFAULT_RESCALE
    const defCm = cmUser || import.meta.env.VITE_TITILER_DEFAULT_COLORMAP
    const rescale = manualUi || autoRescale || (defRescale ? String(defRescale).trim() : '')
    if (rescale) params.set('rescale', rescale)
    if (defCm) params.set('colormap_name', String(defCm).trim())
    return
  }

  const manualEnv = import.meta.env.VITE_TITILER_SNOW_RESCALE?.trim()
  const rescaleFallback = usesEldaPhysicalSnowColormap(styleOverride)
    ? ELDA_SNOW.rescale
    : ELDA_SNOW.rescaleFallbackNonElda
  const rescale = snowRescale ?? manualEnv ?? rescaleFallback

  params.set('rescale', rescale)

  const apply = snowColormapApply ?? { kind: 'name', name: 'elda_snow_mnt' }
  if (apply.kind === 'inline') {
    params.set('colormap', JSON.stringify(eldaSnowTitilerColormap))
  } else {
    params.set('colormap_name', apply.name)
  }

  const nodataVal = resolveSnowNodataQueryParam()
  if (nodataVal != null) {
    params.set('nodata', nodataVal)
  }
}

function appendTitilerTransportParams(params, tilesize = null) {
  // tilesize reste au choix de l'appelant : fetchTitilerRasterSourceConfig le pose déjà lui-même.
  if (tilesize != null && !Number.isNaN(tilesize)) {
    params.set('tilesize', String(tilesize))
  }
  const tilePadding = import.meta.env.VITE_TITILER_TILE_PADDING?.trim()
  if (tilePadding !== '0') params.set('padding', tilePadding || '4')

  const resampling = import.meta.env.VITE_TITILER_RESAMPLING?.trim() || 'bilinear'
  if (resampling !== 'none') params.set('resampling', resampling)

  const reproject = import.meta.env.VITE_TITILER_REPROJECT?.trim() || 'bilinear'
  if (reproject !== 'none') params.set('reproject', reproject)

  const tileFormat = import.meta.env.VITE_TITILER_TILE_FORMAT?.trim() ?? 'png'
  if (tileFormat !== 'none') params.set('tile_format', tileFormat)
}

// build render+transport params for a share link, mirroring the authenticated path
export function buildTitilerRenderParams(layer, styleOverride = null) {
  const params = new URLSearchParams()
  appendTitilerRenderingParams(params, layer, null, styleOverride, null)
  const envTileSize = import.meta.env.VITE_TITILER_TILE_SIZE?.trim()
  const tilesize = envTileSize ? parseInt(envTileSize, 10) : null
  appendTitilerTransportParams(params, tilesize)
  return Object.fromEntries(params)
}

// infer raster tile size in pixels from tilejson or the first tile url query string
function inferTitilerOutputTileSize(firstTileUrl, tilejson) {
  const raw = tilejson?.tileSize ?? tilejson?.tile_size ?? tilejson?.tilesize
  if (raw != null && Number.isFinite(Number(raw))) {
    return Number(raw)
  }
  try {
    const u = new URL(firstTileUrl)
    const ts = u.searchParams.get('tilesize')
    let base = ts ? parseInt(ts, 10) : 512
    const buf = u.searchParams.get('buffer')
    if (buf != null) {
      const b = parseFloat(buf)
      if (!Number.isNaN(b)) {
        base = Math.round(base + 2 * b)
      }
    }
    return base
  } catch {
    return 512
  }
}

// resolve snow rescale min,max string from manual style, env, and titiler statistics
async function resolveSnowLayerRescale(s3Url, base, styleOverride) {
  const manualUi =
    styleOverride?.rescaleMode === 'manual' &&
    typeof styleOverride?.rescaleManual === 'string'
      ? styleOverride.rescaleManual.trim()
      : ''
  const manual = import.meta.env.VITE_TITILER_SNOW_RESCALE?.trim()
  const autoOff = import.meta.env.VITE_TITILER_SNOW_AUTO_RESCALE === 'false'
  const autoStats = import.meta.env.VITE_TITILER_SNOW_AUTO_RESCALE === 'true'
  const physicalElda = usesEldaPhysicalSnowColormap(styleOverride)
  const { rescale: elda, rescaleFallbackNonElda: nonElda } = ELDA_SNOW

  if (manualUi) return manualUi
  if (autoOff) {
    return physicalElda ? manual || elda : manual || nonElda
  }
  if (autoStats) {
    const r = await fetchSnowRescale(s3Url, base)
    if (r != null && r !== '') return r
    return physicalElda ? elda : nonElda
  }
  if (physicalElda) {
    return manual || elda
  }
  const r = await fetchSnowRescale(s3Url, base)
  if (r != null && r !== '') return r
  return manual || nonElda
}

// fetch cog tilejson and build mapbox raster source options (tiles, size, bounds)
export async function fetchTitilerRasterSourceConfig(
  s3Url,
  titilerBase,
  options = {},
) {
  const base = normalizeTitilerBase(titilerBase)
  if (!base || !s3Url || typeof s3Url !== 'string') {
    return null
  }

  const params = new URLSearchParams()
  params.set('url', s3Url.trim())
  const minzoom = options.minzoom ?? 0
  params.set('minzoom', String(minzoom))
  const envTileSize = import.meta.env.VITE_TITILER_TILE_SIZE?.trim()
  const tilesizeOpt =
    options.tilesize ?? (envTileSize ? parseInt(envTileSize, 10) : undefined)
  if (tilesizeOpt != null && !Number.isNaN(tilesizeOpt)) {
    params.set('tilesize', String(tilesizeOpt))
  }

  const styleOverride = options.styleOverride
  const layer = options.layer
  let snowRescale = null
  let snowColormapApply = null

  if (layer && isSnowDepthTitilerLayer(layer)) {
    snowRescale = await resolveSnowLayerRescale(s3Url, base, styleOverride)
    snowColormapApply = await resolveSnowColormapApply(base, styleOverride)
  }

  appendTitilerRenderingParams(
    params,
    layer,
    snowRescale,
    styleOverride,
    snowColormapApply,
  )

  appendTitilerTransportParams(params)

  const tjEndpoint = layer?.is_mosaic ? 'mosaicjson' : 'cog'
  const tilejsonUrl = `${base}/${tjEndpoint}/WebMercatorQuad/tilejson.json?${params.toString()}`
  const res = await fetch(tilejsonUrl)
  if (!res.ok) {
    return null
  }

  const tj = await res.json()
  const tiles = rewriteTitilerTileUrlsForDevProxy(tj.tiles, base)
  const firstTile = tiles?.[0]
  if (!firstTile) {
    return null
  }

  const tileSize = inferTitilerOutputTileSize(firstTile, tj)

  const mapboxRasterResampling =
    import.meta.env.VITE_MAPBOX_RASTER_RESAMPLING?.trim() || 'linear'

  const tilejsonMax =
    tj.maxzoom != null && Number.isFinite(Number(tj.maxzoom))
      ? Number(tj.maxzoom)
      : 22
  const envMax = Number(import.meta.env.VITE_TITILER_MAX_ZOOM)
  const clamp = Number.isFinite(envMax) ? envMax : 22
  const maxzoom = Math.min(tilejsonMax, clamp)

  return {
    tiles,
    tileSize,
    minzoom: tj.minzoom,
    maxzoom,
    bounds: tj.bounds,
    rasterResampling: mapboxRasterResampling,
  }
}

// Test surface (used by jest)
export const __testables = {
  buildParamsForTest(layer, style) {
    const params = new URLSearchParams()
    appendTitilerRenderingParams(params, layer, null, style, null)
    return params
  },
  rewriteTileUrls: rewriteTitilerTileUrlsForDevProxy,
}
